<template>
    <PageContainer>

        <ProductIconsCarousel class="-mx-4 -mt-8 mb-4 md:hidden" />

        <Breadcrumbs
            :breadcrumbs="breadcrumbs"
            class="w-full "
        />

        <h1 class="mb-4 leading-tight md:hidden text-lg">
            {{ product.name }}
        </h1>

        <div class="flex flex-col md:flex-row mb-10 gap-10">
            <div class="w-full lg:w-3/5 flex-grow-0 flex-shrink-0 basis-3/5">
                <ProductImages
                    :images="product.images"
                    :videos="product.videos || []"
                />
            </div>
            <div class="w-full lg:w-2/5 flex flex-grow-0 flex-shrink-0 basis-2/5">
                <ProductInfo :product="product" />
            </div>
        </div>

        <ProductModals
            :product="product"
            class="sm:mb-10"
        />

        <div
            id="reviews"
            class="mb-10 mt-10"
        >
            <div
                :class="[ product.reviews_count > 3 ? 'justify-center-right' : 'justify-center' ]"
                class="flex items-center mb-5"
            >
                <div class="flex">
                    <h4 class="tracking-wide mr-3">
                        REVIEWS
                    </h4>
                    <ProductReviewsScore
                        :product-id="product.id"
                        :score="product.reviews_average_score"
                        :count="product.reviews_count"
                    />
                </div>
                <ProductReviewsPagination
                    v-if="product.reviews_count > 3"
                    v-model="reviewsPage"
                />
            </div>
            <ProductReviews
                :product-id="product.id"
                :reviews-first-page="product.reviews_first_page"
                :reviews-count="product.reviews_count"
                :page="reviewsPage"
                :user-review-date="product.user_review_date"
            />
        </div>

        <div class="mb-10">
            <h4 class="mb-5 text-center tracking-wide">
                YOU MAY ALSO LIKE
            </h4>
            <YouMayAlsoLike :products="product.cross_sells" />
        </div>

        <div class="mb-10">
            <PageInstagramFeed />
        </div>

        <client-only>
            <ProductAddedToBasketModal />
            <ProductCustomisationModal v-if="productHasVariations" />
        </client-only>

    </PageContainer>
</template>

<script>
import ProductAddedToBasketModal from '~/components/ProductAddedToBasketModal';
import Breadcrumbs from '~/components/Breadcrumbs';
import ProductImages from '~/components/ProductImages';
import ProductInfo from '~/components/ProductInfo';
import ProductModals from '~/components/ProductModals';
import YouMayAlsoLike from '~/components/YouMayAlsoLike';
import ProductReviews from '~/components/ProductReviews';
import ProductCustomisationModal from '~/components/ProductCustomisationModal';
import ProductReviewsScore from '~/components/ProductReviewsScore';
import ProductReviewsPagination from '~/components/ProductReviewsPagination';
import PageInstagramFeed from '~/components/PageInstagramFeed';
import PageContainer from '@/components/PageContainer.vue';
import ProductAddOns from '@/components/ProductAddOns.vue';
import ProductIconsCarousel from '../../../components/ProductIconsCarousel.vue';

export default {

    components: {
        ProductIconsCarousel,
        ProductAddOns,
        PageContainer,
        Breadcrumbs,
        ProductImages,
        ProductInfo,
        ProductModals,
        YouMayAlsoLike,
        ProductReviews,
        ProductAddedToBasketModal,
        ProductCustomisationModal,
        ProductReviewsScore,
        ProductReviewsPagination,
        PageInstagramFeed,
    },

    async asyncData({ $axios, params, query, redirect, error, store }) {
        // build the query string
        const queryString = query.preview_token ? `?preview_token=${query.preview_token}` : '';

        await store.dispatch('product/setProductVariations', []);

        // fetch the product
        return $axios.$get(`/products/${params.id}${queryString}`).then((response) => {
            const { product, category } = response;

            store.dispatch('product/resetState');
            store.dispatch('product/setProductVariations', product.variation_types ?? []);
            store.dispatch('product/setFonts', product.fonts);

            // perma redirect to correct the slug if needed
            if (params.slug !== product.slug) {
                redirect(301, `/products/${product.id}/${product.slug}`, query);
            }

            product.cross_sells.forEach((crossSell) => {
                crossSell.cross_sells = product.cross_sells;
            });

            const getBreadcrumbs = () => {
                const breadcrumbs = [{
                    name: 'Home',
                    link: '/',
                }];
                if (category) {
                    if (category.parent) {
                        breadcrumbs.push({
                            name: category.parent.name,
                            link: `/categories/${category.parent.slug}`,
                        });
                    }
                    breadcrumbs.push({
                        name: category.name,
                        link: `/categories/${category.slug}`,
                    });
                }

                return breadcrumbs;
            };

            return {
                product,
                breadcrumbs: getBreadcrumbs(),
            };
        }).catch((e) => $axios.post('pages/missing', {
            path: `/${params.slug}`,
        }).then((response) => {
            redirect(response.data.type, `/${response.data.redirect}`);
        }).catch((err) => error({ statusCode: err.response.status })));
    },

    data() {
        return {
            product: {},
            parentCategory: {},
            breadcrumbs: [],
            reviewsPage: 1,
        };
    },

    computed: {
        productHasVariations() {
            return this.product.variation_types && !!Object.keys(this.product.variation_types).length;
        },
        meta() {
            return [
                { name: 'description', content: this.product.meta_description },
                { name: 'og:title', content: this.product.name },
                { name: 'og:description', content: this.product.meta_description },
                { name: 'og:url', content: this.product.url },
                { name: 'og:image', content: this.product.image_url },
                { name: 'product:id', content: this.product.id },
                { name: 'product:title', content: this.product.name },
                { name: 'product:description', content: this.product.meta_description },
                { name: 'product:availability', content: this.product.in_stock ? 'in stock' : 'out of stock' },
                { name: 'product:condition', content: 'new' },
                { name: 'product:price', content: `${this.product.from_price} GBP` },
                { name: 'product:link', content: this.product.url },
                { name: 'product:image_link', content: this.product.image_url },
                { name: 'product:brand', content: 'Bubblegum Balloons' },
            ];
        },
    },

    mounted() {
        this.$bus.$emit('product-detail', { product: this.product });
        this.$ga.ecommerce.addProduct({
            id: this.product.sku,
            name: this.product.name,
            category: this.product.category,
        });
        this.$ga.ecommerce.setAction('detail');
        this.$ga.event('product', 'detail', 'detail', parseInt(this.product.price, 10));
    },

    head() {
        return {
            title: this.product.meta_title || this.product.name,
            meta: this.meta.map((item) => ({
                hid: item.name,
                name: item.name,
                content: item.content,
            })),
        };
    },

    jsonld() {
        return {
            '@context': 'https://schema.org',
            '@type': 'Product',
            'name': this.product.name,
            'description': this.product.meta_description,
            'offers': {
                '@type': 'Offer',
                'price': this.product.from_price,
                'priceCurrency': 'GBP',
                'availability': `https://schema.org/${this.product.in_stock ? 'InStock' : 'OutOfStock'}`,
            },
            'itemCondition': 'https://schema.org/NewCondition',
            'image': this.product.image_url,
            'url': this.product.url,
            'brand': {
                '@type': 'Brand',
                'name': 'Bubblegum Balloons',
            },
            'sku': this.product.sku,
            'aggregateRating': {
                '@type': 'AggregateRating',
                'ratingValue': this.product.reviews_average_score,
                'reviewCount': this.product.reviews_count,
            },
        };
    },

};
</script>

<style lang="scss" scoped>
</style>
